import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { nanoid } from 'nanoid';
import Toast, { type ToastData } from '@/components/ui/Toast';
import ConfirmModal from '@/components/ui/ConfirmModal';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ConfirmOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning';
}

interface NotificationContextValue {
  notify: (message: string, type?: ToastType) => void;
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

const severityMap: Record<ToastType, ToastData['severity']> = {
  error: 'CRITICAL',
  warning: 'HIGH',
  info: 'MEDIUM',
  success: 'LOW',
};

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastData[]>([]);
  const [confirmState, setConfirmState] = useState<{
    options: ConfirmOptions;
    resolve: (value: boolean) => void;
  } | null>(null);

  const notify = useCallback((message: string, type: ToastType = 'info') => {
    const id = nanoid();
    setToasts(prev => [...prev, {
      id,
      title: message,
      severity: severityMap[type],
    }]);
  }, []);

  const confirmFn = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirmState({ options, resolve });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    confirmState?.resolve(true);
    setConfirmState(null);
  }, [confirmState]);

  const handleCancel = useCallback(() => {
    confirmState?.resolve(false);
    setConfirmState(null);
  }, [confirmState]);

  const handleCloseToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <NotificationContext.Provider value={{ notify, confirm: confirmFn }}>
      {children}
      <Toast toasts={toasts} onClose={handleCloseToast} />
      {confirmState && (
        <ConfirmModal
          isOpen={true}
          title={confirmState.options.title}
          message={confirmState.options.message}
          confirmText={confirmState.options.confirmText}
          cancelText={confirmState.options.cancelText}
          variant={confirmState.options.variant}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
}
